import React from "react";
import { SpotShapeType } from "../../../../types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "../../../ui/dialog";
import { EditorField, EditorFieldGrid } from "../../../ui/EditorField";
import { EditorSelect } from "../../../ui/EditorSelect";

export interface GridConfig {
  rows: number;
  columns: number;
  gap: number;
  spotType: SpotShapeType;
  addSpots: boolean;
}

export interface GridPromptProps {
  /** The dragged rectangle's size, used to seed sensible row/column defaults. */
  rect: { width: number; height: number };
  onConfirm: (config: GridConfig) => void;
  onCancel: () => void;
}

const DEFAULT_CELL_SIZE = 100;

const TYPE_OPTIONS = [
  { value: SpotShapeType.ParkingSpace, label: "Parking Space" },
  { value: SpotShapeType.Parking, label: "Parking Lot" },
  { value: SpotShapeType.Desk, label: "Desk" },
  { value: SpotShapeType.Room, label: "Meeting Room" }
];

/**
 * Collects rows/columns/spacing/type for the grid tool once its bounding
 * rectangle has been dragged out — same in-app-dialog pattern as
 * MeasurementPrompt, triggered from a drag instead of a two-click gesture.
 * The grid's painted lines (drawn as wall-path shapes) are always created;
 * placing a bookable spot in every cell is an optional extra.
 */
export const GridPrompt: React.FC<GridPromptProps> = ({
  rect,
  onConfirm,
  onCancel
}) => {
  const [rows, setRows] = React.useState(() =>
    Math.max(1, Math.round(rect.height / DEFAULT_CELL_SIZE))
  );
  const [columns, setColumns] = React.useState(() =>
    Math.max(1, Math.round(rect.width / DEFAULT_CELL_SIZE))
  );
  const [gap, setGap] = React.useState(0);
  const [spotType, setSpotType] = React.useState<SpotShapeType>(
    SpotShapeType.ParkingSpace
  );
  const [addSpots, setAddSpots] = React.useState(true);

  const cellWidth = (rect.width - (columns - 1) * gap) / columns;
  const cellHeight = (rect.height - (rows - 1) * gap) / rows;
  const isValid =
    rows >= 1 &&
    columns >= 1 &&
    Number.isFinite(cellWidth) &&
    cellWidth > 0 &&
    Number.isFinite(cellHeight) &&
    cellHeight > 0;

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm({ rows, columns, gap, spotType, addSpots });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>Create Grid</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <EditorFieldGrid>
            <EditorField
              label="Rows"
              fieldKey="Rows"
              type="number"
              min={1}
              value={rows}
              onChange={(e) =>
                setRows(Math.max(1, Number(e.target.value) || 1))
              }
            />
            <EditorField
              label="Columns"
              fieldKey="Cols"
              type="number"
              min={1}
              value={columns}
              onChange={(e) =>
                setColumns(Math.max(1, Number(e.target.value) || 1))
              }
            />
          </EditorFieldGrid>
          <EditorField
            label="Gap between lines (px)"
            fieldKey="Gap"
            type="number"
            min={0}
            value={gap}
            onChange={(e) => setGap(Math.max(0, Number(e.target.value) || 0))}
          />
          <label className="flex items-center gap-2 text-neutral-600 text-xs">
            <input
              type="checkbox"
              checked={addSpots}
              onChange={(e) => setAddSpots(e.target.checked)}
            />
            Also add a bookable spot in every cell
          </label>
          {addSpots && (
            <div className="flex flex-col gap-1">
              <span className="text-neutral-600 text-xs">Type</span>
              <EditorSelect
                label="Type"
                value={spotType}
                onValueChange={(value) => setSpotType(value as SpotShapeType)}
                options={TYPE_OPTIONS}
              />
            </div>
          )}
          <p className="text-neutral-500 text-xs">
            {isValid
              ? `${rows} x ${columns} grid, ${Math.round(cellWidth)}×${Math.round(cellHeight)}px per cell${addSpots ? ` — ${rows * columns} spots` : ""}.`
              : "Increase the drawn area or reduce rows/columns/gap."}
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md px-3 py-1.5 text-neutral-600 text-xs hover:bg-neutral-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!isValid}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-white text-xs hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Create Grid
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
